import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { authenticate, login } from "../feature/auth/authAPI";
import { Link, useNavigate } from "react-router-dom";
import Loader from "../Components/loader";
import { Video, Mail, Lock, AlertCircle, CheckCircle2, ArrowRight } from "lucide-react";

export default function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, authenticated } = useSelector((state) => state.auth);
  const { loading, error, success: msg } = useSelector((state) => state.auth.login);
  const { loading: authLoading } = useSelector((state) => state.auth.authenticate);

  const [form, setForm] = useState({
    email: "",
    password: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(login(form));
    dispatch(authenticate());
  };

  useEffect(() => {
    dispatch(authenticate());
  }, [authenticated, user]);

  useEffect(() => {
    document.title = "Interviewly - Login";
  }, []);

  useEffect(() => {
    if (authenticated && user) {
      navigate("/dashboard");
    }
  }, [user, msg, authenticated, loading, error]);

  return loading || authLoading ? (
    <Loader />
  ) : (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-cocoa-800 px-4 py-12 sm:px-6">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-80 bg-[radial-gradient(ellipse_at_top,rgba(251,191,36,0.1),transparent_60%)]"
        aria-hidden="true"
      />
      <div className="relative w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-400 text-cocoa-900 shadow-gold-inset">
            <Video className="h-7 w-7" strokeWidth={2.25} />
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-cream-100 sm:text-4xl">
            Welcome back
          </h1>
          <p className="mx-auto mt-3 max-w-md text-base leading-relaxed text-amber-100/60">
            Log in to your account to access the{" "}
            <span className="font-semibold text-amber-200">Interviewly Platform</span>.
          </p>
        </div>

        {(msg || error) && (
          <div className="mb-5 flex flex-col gap-2">
            {msg && (
              <div className="flex items-start gap-2.5 rounded-xl border border-emerald-400/25 bg-emerald-400/10 px-4 py-3 text-sm font-medium text-emerald-300 animate-fade-in">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                {msg}
              </div>
            )}
            {error && (
              <div className="flex items-start gap-2.5 rounded-xl border border-red-400/25 bg-red-400/10 px-4 py-3 text-sm font-medium text-red-300 animate-fade-in">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                {error}
              </div>
            )}
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          className="card animate-fade-in-up p-7 shadow-modal sm:p-8"
        >
          <div className="mb-6">
            <h2 className="text-xl font-semibold tracking-tight text-cream-100">
              Sign in to your account
            </h2>
            <p className="mt-1 text-sm text-amber-100/50">
              Enter your credentials to continue
            </p>
          </div>

          <div className="flex flex-col gap-5">
            <div>
              <label htmlFor="email" className="label">
                Email
              </label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-amber-200/40" />
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  className="input pl-10"
                  placeholder="you@company.com"
                  autoComplete="email"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="label">
                Password
              </label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-amber-200/40" />
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={form.password}
                  onChange={handleChange}
                  className="input pl-10"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
              </div>
            </div>
          </div>

          <button type="submit" className="btn btn-primary btn-lg mt-7 w-full">
            Login
            <ArrowRight className="h-4 w-4" />
          </button>

          <p className="mt-6 text-center text-sm text-amber-100/50">
            Don&apos;t have an account?{" "}
            <Link
              to="/signup"
              className="font-semibold text-amber-300 transition-colors hover:text-amber-200"
            >
              Sign up
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
